import { auth } from '@/firebaseConfig';
import { LogMeta, Severity } from './types';
import {
  captureSentryException,
  clearSentryUserProperties,
  sendSentryBreadcrumb,
  sendSentryLog,
  setSentryUserProperties,
} from './sentry';
import { logToConsole } from './telemetryConsole';
import {
  clearAnalyticsUserProperties,
  sendAnalyticsEvent,
  setAnalyticsUserProperties,
} from './analytics';
import { v4 as uuidv4 } from 'uuid';

// Runtime-scoped identifier (new on each page load / app bootstrap).
// We log this alongside tabId so we can distinguish:
// - same browser tab after refresh/reload (same tabId, new tabRuntimeId)
// - duplicated tabs opened from an existing tab (same tabId, different tabRuntimeId)
const tabRuntimeId = uuidv4();

const tabId = (() => {
  try {
    const k = 'practist_tab_id';
    const existing = sessionStorage.getItem(k);
    if (existing) return existing;
    const id = uuidv4();
    sessionStorage.setItem(k, id);
    return id;
  } catch {
    return undefined;
  }
})();

const getFirebaseAuthContext = () => {
  if (!auth.currentUser) return undefined;

  return {
    // `auth.currentUser.toJSON()` includes a bunch of info that we don't want
    // to be logged (access tokens, etc.), so pick and choose what we want here.
    currentUser: {
      uid: auth.currentUser.uid,
      email: auth.currentUser.email,
      tenantId: auth.currentUser.tenantId,
      metadata: {
        creationTime: auth.currentUser.metadata.creationTime,
        lastSignInTime: auth.currentUser.metadata.lastSignInTime,
      },
      displayName: auth.currentUser.displayName,
    },
  };
};

const getMemoryUsage = () => {
  if ('memory' in performance) {
    // Chrome non-standard
    const m = (performance as any).memory;
    return {
      usedJSHeapSize: m.usedJSHeapSize,
      jsHeapSizeLimit: m.jsHeapSizeLimit,
    };
  }
};

const getContext = () => ({
  tabId,
  tabRuntimeId,
  userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
  frontendAuth: getFirebaseAuthContext(),
  environment: process.env.NODE_ENV,
  logId: uuidv4(),
  memoryUsage: getMemoryUsage(),
});

const addContext = (extra?: LogMeta) => ({
  ...extra,
  ...getContext(),
});

export const telemetry = {
  /**
   * Logs an info message to the console and Sentry.
   * @param props - The properties of the message.
   * @param props.message - The message to log.
   * @param props.extra - Extra properties to add to the message.
   */
  info: (props: { message: string; extra?: LogMeta }) => {
    const { message, extra } = props;
    const extraWithContext = addContext(extra);

    logToConsole({
      severity: 'INFO',
      message,
      extra: extraWithContext,
    });
    sendSentryLog({
      severity: 'INFO',
      message,
      extra: extraWithContext,
    });
    sendSentryBreadcrumb({
      message,
      severity: 'INFO',
      extra: extraWithContext,
    });
  },
  /**
   * Logs a warning message to the console and Sentry.
   * @param props - The properties of the message.
   * @param props.message - The message to log.
   * @param props.extra - Extra properties to add to the message.
   */
  warn: (props: { message: string; extra?: LogMeta }) => {
    const { message, extra } = props;
    const extraWithContext = addContext(extra);
    logToConsole({
      severity: 'WARNING',
      message,
      extra: extraWithContext,
    });
    sendSentryLog({
      severity: 'WARNING',
      message,
      extra: extraWithContext,
    });
    sendSentryBreadcrumb({
      severity: 'WARNING',
      message,
      extra: extraWithContext,
    });
  },
  /**
   * Logs an error message to the console and Sentry.
   * @param props - The properties of the message.
   * @param props.error - The error to log.
   * @param props.message - The message to log.
   * @param props.extra - Extra properties to add to the message.
   */
  error: (props: { error: unknown; message?: string; extra?: LogMeta }) => {
    const { error, extra } = props;
    const message = props.message ?? 'firms_app_error';
    const extraWithContext = addContext(extra);
    logToConsole({
      severity: 'ERROR',
      message,
      extra: {
        ...extraWithContext,
        errorMessage: String(error),
      },
    });
    sendSentryLog({
      message,
      severity: 'ERROR',
      extra: {
        ...extraWithContext,
        errorMessage: String(error),
      },
    });
    captureSentryException(error, { extra: extraWithContext });
  },
  /**
   * Sends an analytics event to Google Analytics, and logs it to the console and Sentry.
   * @param props - The properties of the event.
   * @param props.name - The name of the event.
   * @param props.extra - Extra properties to add to the event.
   */
  analyticsEvent: (props: { name: string; extra?: LogMeta }) => {
    const { name, extra } = props;
    const extraWithContext = addContext(extra);
    logToConsole({
      severity: 'INFO',
      message: `Analytics event: ${name}`,
      extra: extraWithContext,
    });
    sendSentryLog({
      severity: 'INFO',
      message: `Analytics event: ${name}`,
      extra: extraWithContext,
    });
    sendSentryBreadcrumb({
      severity: 'INFO',
      message: `Analytics event: ${name}`,
      extra: extraWithContext,
    });
    sendAnalyticsEvent(name, extraWithContext);
  },
  /**
   * Sets user properties in Google Analytics and Sentry.
   * @param props - The properties of the user.
   * @param props.userIds - The user IDs.
   * @param props.email - The user's email.
   * @param props.extra - Extra properties to add to the user.
   */
  setUserProperties: (props: {
    userIds: { auth: string; firestore: string };
    email: string;
    tenantId: string;
  }) => {
    const { userIds, email, tenantId } = props;
    const extraWithContext = addContext({ userIds, email, tenantId });
    logToConsole({
      severity: 'INFO',
      message: 'Will set user properties',
      extra: extraWithContext,
    });
    sendSentryLog({
      severity: 'INFO',
      message: 'Will set user properties',
      extra: extraWithContext,
    });
    setSentryUserProperties({
      userIds,
      email,
      tenantId,
    });
    setAnalyticsUserProperties({
      userIds,
      email,
      tenantId,
    });
  },
  clearUserProperties: () => {
    logToConsole({
      severity: 'INFO',
      message: 'Will clear user properties',
      extra: getContext(),
    });
    sendSentryLog({
      severity: 'INFO',
      message: 'Will clear user properties',
      extra: getContext(),
    });
    clearAnalyticsUserProperties();
    clearSentryUserProperties();
  },
};

export type { Severity, LogMeta };
